/**
 * LUT处理着色器模板
 * 支持灰度遮罩和可配置的工作组大小
 */
export const lutComputeShaderTemplate = (workgroupSizeX, workgroupSizeY) => `
struct Params {
    intensity: f32,      // LUT 效果强度
    lutSize: f32,        // LUT 尺寸
    maskIntensity: f32,  // 遮罩强度
    enableMask: f32,     // 是否启用遮罩 (0.0 = 禁用, 1.0 = 启用)
    maskOffsetX: f32,    // 遮罩偏移X坐标
    maskOffsetY: f32,    // 遮罩偏移Y坐标
    maskWidth: f32,      // 遮罩宽度
    maskHeight: f32      // 遮罩高度
}

@group(0) @binding(0) var inputTexture: texture_2d<f32>;           // 输入图像纹理
@group(0) @binding(1) var lutTexture: texture_3d<f32>;             // 3D LUT 纹理
@group(0) @binding(2) var outputTexture: texture_storage_2d<rgba8unorm, write>; // 输出图像纹理
@group(0) @binding(3) var lutSampler: sampler;                   // LUT 纹理采样器
@group(0) @binding(4) var maskTexture: texture_2d<f32>;          // 遮罩纹理
@group(0) @binding(5) var maskSampler: sampler;                  // 遮罩纹理采样器
@group(0) @binding(6) var<uniform> params: Params;               // Uniform 参数

// 使用模板变量配置工作组大小
@compute @workgroup_size(${workgroupSizeX}, ${workgroupSizeY})
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
    let dims = textureDimensions(inputTexture);
    let coords = vec2<u32>(global_id.xy);

    // 边界检查
    if (coords.x >= dims.x || coords.y >= dims.y) {
        return;
    }

    // 加载原始像素颜色
    let originalColor = textureLoad(inputTexture, vec2<i32>(coords), 0);
    
    // 计算LUT坐标
    let lutSizeMinusOne = params.lutSize - 1.0;
    let lutSizeRecip = 1.0 / params.lutSize;
    let halfLutSizeRecip = 0.5 * lutSizeRecip;
    
    let lutCoord = originalColor.rgb * (lutSizeMinusOne * lutSizeRecip) + halfLutSizeRecip;

    // 采样LUT颜色
    let lutColor = textureSampleLevel(lutTexture, lutSampler, lutCoord, 0.0);

    // 计算遮罩强度
    var finalIntensity = params.intensity;
    
    // 如果启用遮罩，从遮罩纹理采样并计算最终强度
    if (params.enableMask > 0.5) {
        // 计算全局坐标：分片偏移 + 分片内坐标
        let globalX = params.maskOffsetX + f32(coords.x);
        let globalY = params.maskOffsetY + f32(coords.y);
        
        // 将全局坐标映射到遮罩纹理坐标
        // 注意：这里假设遮罩纹理的尺寸与原始图像相同
        let maskCoordX = globalX / f32(textureDimensions(maskTexture).x);
        let maskCoordY = globalY / f32(textureDimensions(maskTexture).y);
        
        // 边界检查，确保坐标在有效范围内
        if (maskCoordX >= 0.0 && maskCoordX <= 1.0 && maskCoordY >= 0.0 && maskCoordY <= 1.0) {
            let maskColor = textureSampleLevel(maskTexture, maskSampler, vec2<f32>(maskCoordX, maskCoordY), 0.0);
            // 使用遮罩的灰度值（R通道）作为强度因子
            let maskValue = maskColor.r;
            // 遮罩强度应该反向：白色区域无效果，黑色区域有效果
            finalIntensity = params.intensity * (1.0 - maskValue) * params.maskIntensity;
        }
    }

    // 混合原始颜色和LUT颜色
    let newColor = vec4<f32>(
        mix(originalColor.rgb, lutColor.rgb, finalIntensity),
        originalColor.a
    );

    // 写入输出纹理
    textureStore(outputTexture, coords, newColor);
}
`;
/**
 * 标准LUT处理着色器（使用16x16工作组）
 * 适用于大多数WebGPU实现
 */
export const lutComputeShader = lutComputeShaderTemplate(16, 16);
/**
 * 高性能LUT处理着色器（使用32x32工作组）
 * 适用于支持更大工作组的现代GPU
 */
export const lutComputeShaderHighPerformance = lutComputeShaderTemplate(32, 32);
/**
 * 创建计算着色器模块
 * @param device WebGPU设备
 * @param highPerformance 是否使用高性能模式
 * @returns 着色器模块
 */
export function createLUTComputeShader(device, highPerformance = false) {
    const shaderCode = highPerformance ? lutComputeShaderHighPerformance : lutComputeShader;
    return device.createShaderModule({
        code: shaderCode,
        label: highPerformance ? 'LUT Compute Shader (High Performance)' : 'LUT Compute Shader (Optimized)'
    });
}
/**
 * 简单的图像复制着色器（用于测试）
 */
export const copyComputeShader = `
@group(0) @binding(0) var inputTexture: texture_2d<f32>;
@group(0) @binding(1) var outputTexture: texture_storage_2d<rgba8unorm, write>;

@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
    let dimensions = textureDimensions(inputTexture);
    
    if (global_id.x >= dimensions.x || global_id.y >= dimensions.y) {
        return;
    }
    
    let coord = vec2<i32>(global_id.xy);
    let color = textureLoad(inputTexture, coord, 0);
    textureStore(outputTexture, coord, color);
}
`;
/**
 * 创建图像复制着色器模块
 * @param device WebGPU设备
 * @returns 着色器模块
 */
export function createCopyComputeShader(device) {
    return device.createShaderModule({
        code: copyComputeShader,
        label: 'Copy Compute Shader (Optimized)'
    });
}
//# sourceMappingURL=shaders.js.map